import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { 
  FileText, 
  RefreshCw, 
  CheckCircle2, 
  Trophy, 
  TrendingDown, 
  ArrowLeft,
  Users,
  Award,
  Check,
  X,
  UserPlus,
  AlertTriangle
} from "lucide-react";
import { AddSupplierInvites } from "@/components/AddSupplierInvites";
import { ActiveVendorsPanel } from "@/components/auction/ActiveVendorsPanel";
import { AuctionCountdown } from "@/components/auction/AuctionCountdown";
import { ReviseOfferDialog } from "@/components/auction/ReviseOfferDialog";
import { formatInLondon, formatFullDateTime } from "@/lib/timezone";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RFP {
  id: string;
  title: string;
  description: string;
  category: string | null;
  budget_range: string | null;
  created_at: string;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  min_decrement: number | null;
  min_bid: number | null;
  status: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
}

interface Proposal {
  id: string;
  supplier_company_name: string;
  initial_bid_amount: number;
  submitted_at: string;
  status: string;
  contact_name: string;
  contact_email: string;
  proposal_details: string | null;
}

interface Bid {
  id: string;
  proposal_id: string;
  amount: number;
  created_at: string;
  source: string;
  status: string;
  proposal?: {
    supplier_company_name: string;
  };
}

interface SupplierInvitation {
  id: string;
  supplier_name: string;
  supplier_email: string;
}

interface RankedProposal extends Proposal {
  currentBid: number;
  requestedBid: number | null;
  originalBid: number;
  lastUpdated: string;
  rank: number;
  savingsPercent: number;
  isSelected: boolean;
  bidCount: number;
}

const AuctionTimelineStepper = ({ rfp }: { rfp: RFP | null }) => {
  if (!rfp) return null;

  const now = new Date();
  const createdAt = new Date(rfp.created_at);
  const deadline = new Date(rfp.submission_deadline);
  const auctionStart = new Date(rfp.auction_start);
  const auctionEnd = new Date(rfp.auction_end);

  const steps = [
    { label: "Request Published", date: createdAt, completed: true },
    { label: "Proposals Due", date: deadline, completed: now >= deadline },
    { label: "Auction Start", date: auctionStart, completed: now >= auctionStart },
    { label: "Auction End", date: auctionEnd, completed: now >= auctionEnd },
  ];

  return (
    <Card className="mb-6 bg-card">
      <CardContent className="py-6">
        <div className="flex items-center justify-between relative">
          {steps.map((step, index) => (
            <div key={step.label} className="flex flex-col items-center z-10 flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step.completed
                    ? "bg-auction-blue text-white"
                    : "bg-muted text-muted-foreground border-2 border-dashed border-auction-blue"
                }`}
              >
                {step.completed ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
              </div>
              <p className="mt-2 text-sm font-medium text-foreground">{step.label}</p>
              <p className="text-xs text-muted-foreground">
                {format(step.date, "MMM d, yyyy")}
              </p>
              <p className="text-xs text-muted-foreground">
                {format(step.date, "h:mm a")}
              </p>
              {index < steps.length - 1 && (
                <div
                  className={`absolute top-5 h-0.5 border-t-2 border-dashed ${
                    steps[index + 1].completed ? "border-auction-blue" : "border-auction-blue/40"
                  }`}
                  style={{
                    left: `calc(${(index + 0.5) * 25}% + 20px)`,
                    width: `calc(25% - 40px)`,
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const BidCard = ({
  proposal,
  onViewProposal,
  onRevisedOffer,
  onSelect,
  isSelecting,
  selectedProposalId,
}: {
  proposal: RankedProposal;
  onViewProposal: (id: string) => void;
  onRevisedOffer: (id: string) => void;
  onSelect: (id: string) => void;
  isSelecting: boolean;
  selectedProposalId: string | null;
}) => {
  const isTopRank = proposal.rank === 1;
  const hasWinner = selectedProposalId !== null;
  const isWinner = proposal.id === selectedProposalId;
  const isLoser = hasWinner && !isWinner;

  // Get truncated summary (first 2 lines only)
  const getTruncatedSummary = () => {
    try {
      const details = typeof proposal.proposal_details === 'string' 
        ? JSON.parse(proposal.proposal_details) 
        : proposal.proposal_details;
      const text = details?.summary || details?.scope || proposal.proposal_details || "";
      const lines = text.split('\n').slice(0, 2).join('\n');
      return lines.length < text.length ? lines + '...' : lines;
    } catch {
      const text = proposal.proposal_details || "";
      const lines = text.split('\n').slice(0, 2).join('\n');
      return lines.length < text.length ? lines + '...' : lines;
    }
  };

  // Check if there's a pending requested offer
  const hasPendingRequest = proposal.requestedBid !== null;

  return (
    <Card
      className={`relative transition-all duration-300 hover:shadow-lg min-w-[340px] ${
        isWinner
          ? "border-2 border-green-500 bg-green-50/30 shadow-lg"
          : isLoser
          ? "border-2 border-red-300 bg-red-50/30 opacity-75"
          : proposal.isSelected
          ? "border-2 border-auction-selected shadow-lg"
          : "border border-border"
      } ${isTopRank && !hasWinner ? "animate-pulse-ring" : ""}`}
    >
      {isTopRank && !hasWinner && (
        <div className="absolute -top-3 left-4">
          <Badge className="bg-auction-blue text-white flex items-center gap-1">
            <Trophy className="w-3 h-3" />
            Leading Bid
          </Badge>
        </div>
      )}
      {isWinner && (
        <div className="absolute -top-3 left-4">
          <Badge className="bg-green-500 text-white flex items-center gap-1">
            <Award className="w-3 h-3" />
            Winner
          </Badge>
        </div>
      )}
      {isLoser && (
        <div className="absolute -top-3 left-4">
          <Badge variant="destructive" className="flex items-center gap-1">
            <X className="w-3 h-3" />
            Not Selected
          </Badge>
        </div>
      )}

      <CardHeader className="pb-3 pt-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Rank</p>
            <div className="flex items-center gap-3">
              <span
                className={`text-2xl font-bold ${
                  isWinner ? "text-green-600" : isLoser ? "text-red-400" : isTopRank ? "text-auction-blue" : "text-foreground"
                }`}
              >
                #{proposal.rank}
              </span>
              <h3 className="text-lg font-semibold text-foreground">
                {proposal.supplier_company_name}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {proposal.contact_name} • {proposal.contact_email}
            </p>
          </div>

          <div className="flex flex-col items-end gap-1">
            <Badge
              variant="secondary"
              className="bg-auction-green/10 text-auction-green flex items-center gap-1"
            >
              <TrendingDown className="w-3 h-3" />
              {proposal.savingsPercent.toFixed(1)}% savings
            </Badge>
            <span className="text-xs text-muted-foreground">
              {proposal.bidCount} bid{proposal.bidCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-0">
        {/* Current confirmed bid - always show */}
        <div className="flex items-baseline gap-3">
          <span className={`text-3xl font-bold ${isWinner ? "text-green-600" : isLoser ? "text-red-400" : "text-foreground"}`}>
            ${proposal.currentBid.toLocaleString()}
          </span>
          {proposal.currentBid !== proposal.originalBid && (
            <span className="text-lg text-muted-foreground line-through">
              ${proposal.originalBid.toLocaleString()}
            </span>
          )}
        </div>

        {/* Requested offer highlight if there's a pending request */}
        {proposal.requestedBid !== null && (
          <div className="rounded-lg border-2 border-orange-400 bg-orange-50/30 p-3">
            <p className="text-xs font-medium text-orange-600 mb-1">Requested Offer (Awaiting Response)</p>
            <div className="flex items-baseline gap-3">
              <span className="text-2xl font-bold text-orange-700">
                ${proposal.requestedBid.toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-orange-600 mt-2">
              Waiting for supplier to accept or reject this offer
            </p>
          </div>
        )}

        {proposal.proposal_details && (
          <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
            {getTruncatedSummary()}
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Last updated: {format(new Date(proposal.lastUpdated), "MMM d, yyyy h:mm a")}
        </p>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewProposal(proposal.id)}
          >
            <FileText className="w-4 h-4 mr-1" />
            View RFP
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRevisedOffer(proposal.id)}
            disabled={isWinner || isLoser}
            className={isWinner || isLoser ? "opacity-50 cursor-not-allowed" : ""}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Revised Offer
          </Button>
          <Button
            size="sm"
            onClick={() => onSelect(proposal.id)}
            disabled={isSelecting || hasWinner}
            className={`text-white ${
              hasWinner && !isWinner 
                ? "opacity-50 cursor-not-allowed bg-gray-400 hover:bg-gray-400" 
                : isWinner 
                ? "bg-green-500 hover:bg-green-500/90" 
                : "bg-auction-blue hover:bg-auction-blue/90"
            }`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {isWinner ? "Selected" : "Select"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ReverseAuctionView = () => {
  const { id: rfpId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rfp, setRfp] = useState<RFP | null>(null);
  const [proposals, setProposals] = useState<RankedProposal[]>([]);
  const [bids, setBids] = useState<Bid[]>([]);
  const [invitations, setInvitations] = useState<SupplierInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const [reviseOfferData, setReviseOfferData] = useState<{
    open: boolean;
    proposalId: string;
    currentBid: number;
    supplierName: string;
  }>({ open: false, proposalId: "", currentBid: 0, supplierName: "" });
  const [requestedOfferWarning, setRequestedOfferWarning] = useState<{
    open: boolean;
    proposalId: string;
    supplierName: string;
    originalBid: number;
    requestedBid: number;
  }>({ open: false, proposalId: "", supplierName: "", originalBid: 0, requestedBid: 0 });

  useEffect(() => {
    if (rfpId) {
      fetchData();

      // Subscribe to real-time bid updates
      const channel = supabase
        .channel(`rfp-bids-${rfpId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "bids",
            filter: `rfp_id=eq.${rfpId}`,
          },
          (payload) => {
            console.log("New bid received:", payload);
            fetchData();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [rfpId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch RFP details
      const { data: rfpData, error: rfpError } = await supabase
        .from("rfps")
        .select("*")
        .eq("id", rfpId)
        .maybeSingle();

      if (rfpError) throw rfpError;
      setRfp(rfpData);

      // Fetch proposals for this RFP
      const { data: proposalsData, error: proposalsError } = await supabase
        .from("proposals")
        .select("*")
        .eq("rfp_id", rfpId);

      if (proposalsError) throw proposalsError;

      // Fetch supplier invitations for this RFP
      const { data: invitationsData, error: invitationsError } = await supabase
        .from("supplier_invitations")
        .select("id, supplier_name, supplier_email")
        .eq("rfp_id", rfpId);

      if (invitationsError) throw invitationsError;
      setInvitations(invitationsData || []);

      if (!proposalsData || proposalsData.length === 0) {
        setProposals([]);
        setBids([]);
        return;
      }

      // Fetch winning proposal decision if exists
      const { data: decisionData } = await supabase
        .from("rfp_decisions")
        .select("winning_proposal_id")
        .eq("rfp_id", rfpId)
        .maybeSingle();

      const winningProposalId = decisionData?.winning_proposal_id;
      
      // Fetch all bids for these proposals with supplier info
      const proposalIds = proposalsData.map((p) => p.id);
      const { data: bidsData, error: bidsError } = await supabase
        .from("bids")
        .select(`
          *,
          proposal:proposals(supplier_company_name)
        `)
        .in("proposal_id", proposalIds)
        .order("created_at", { ascending: false });

      if (bidsError) throw bidsError;
      setBids(bidsData || []);

      // Process proposals with bids
      const rankedProposals: RankedProposal[] = proposalsData.map((proposal) => {
        const proposalBids = (bidsData || []).filter((b) => b.proposal_id === proposal.id);
        
        // Separate confirmed and requested bids
        const confirmedBids = proposalBids.filter((b) => b.status === 'confirmed');
        const requestedBids = proposalBids.filter((b) => b.status === 'requested');
        
        // Current bid is the lowest confirmed bid or initial bid
        const latestConfirmedBid = confirmedBids[0];
        const currentBid = latestConfirmedBid ? latestConfirmedBid.amount : proposal.initial_bid_amount;
        
        // Requested bid is the latest requested offer (if any)
        const latestRequestedBid = requestedBids[0];
        const requestedBid = latestRequestedBid ? latestRequestedBid.amount : null;
        
        const originalBid = proposal.initial_bid_amount;
        const savingsPercent = ((originalBid - currentBid) / originalBid) * 100;

        return {
          ...proposal,
          currentBid,
          requestedBid,
          originalBid,
          lastUpdated: latestRequestedBid ? latestRequestedBid.created_at : (latestConfirmedBid ? latestConfirmedBid.created_at : proposal.submitted_at),
          rank: 0,
          savingsPercent: Math.max(0, savingsPercent),
          isSelected: proposal.id === winningProposalId,
          bidCount: proposalBids.length,
        };
      });

      // Sort by current bid (ascending) and assign ranks
      rankedProposals.sort((a, b) => a.currentBid - b.currentBid);
      rankedProposals.forEach((p, index) => {
        p.rank = index + 1;
      });

      setProposals(rankedProposals);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast({
        title: "Error",
        description: "Failed to load auction data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewProposal = (proposalId: string) => {
    navigate(`/rfp/${rfpId}/view`);
  };

  const handleRevisedOffer = (proposalId: string) => {
    const proposal = proposals.find(p => p.id === proposalId);
    if (proposal) {
      setReviseOfferData({
        open: true,
        proposalId: proposal.id,
        currentBid: proposal.currentBid,
        supplierName: proposal.supplier_company_name,
      });
    }
  };

  const handleSelectClick = (proposalId: string) => {
    // Check if this proposal has a pending requested offer
    const proposal = proposals.find(p => p.id === proposalId);
    if (proposal && proposal.requestedBid !== null) {
      // Show warning dialog
      setRequestedOfferWarning({
        open: true,
        proposalId,
        supplierName: proposal.supplier_company_name,
        originalBid: proposal.currentBid,
        requestedBid: proposal.requestedBid,
      });
    } else {
      // No pending request, proceed directly
      handleSelect(proposalId);
    }
  };

  const handleSelect = async (proposalId: string) => {
    try {
      setIsSelecting(true);

      // Update winning proposal to successful
      const { error: winningError } = await supabase
        .from("proposals")
        .update({ selection_status: "successful" })
        .eq("id", proposalId);

      if (winningError) throw winningError;

      // Update all other proposals to unsuccessful
      const { error: losingError } = await supabase
        .from("proposals")
        .update({ selection_status: "unsuccessful" })
        .eq("rfp_id", rfpId)
        .neq("id", proposalId);

      if (losingError) throw losingError;

      // Record decision
      const { error: decisionError } = await supabase.from("rfp_decisions").upsert(
        {
          rfp_id: rfpId,
          winning_proposal_id: proposalId,
          notes: "Selected via Reverse Auction View",
        },
        { onConflict: "rfp_id" }
      );

      if (decisionError) throw decisionError;

      // Update RFP status to COMPLETED
      await supabase
        .from("rfps")
        .update({ status: "COMPLETED" })
        .eq("id", rfpId);

      // Update local state
      setProposals((prev) =>
        prev.map((p) => ({
          ...p,
          isSelected: p.id === proposalId,
        }))
      );

      toast({
        title: "Winner Selected",
        description: "The winning firm has been recorded and all other proposals marked as unsuccessful.",
      });
      
      fetchData();
    } catch (error) {
      console.error("Error selecting proposal:", error);
      toast({
        title: "Error",
        description: "Failed to select firm",
        variant: "destructive",
      });
    } finally {
      setIsSelecting(false);
    }
  };

  const getLowestBid = () => {
    if (proposals.length === 0) return 0;
    return Math.min(...proposals.map(p => p.currentBid));
  };

  const isAuctionActive = rfp?.status === "AUCTION_ACTIVE";
  const isAuctionEnded = rfp?.status === "AUCTION_ENDED" || rfp?.status === "COMPLETED";

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/active")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-foreground">{rfp?.matter_name || rfp?.title || "Reverse Auction"}</h2>
          <p className="text-muted-foreground line-clamp-2">{rfp?.description}</p>
          {(rfp?.region || rfp?.team) && (
            <div className="flex gap-2 mt-2">
              {rfp?.region && <Badge variant="outline">{rfp.region.toUpperCase()}</Badge>}
              {rfp?.team && <Badge variant="outline">{rfp.team.charAt(0).toUpperCase() + rfp.team.slice(1)}</Badge>}
            </div>
          )}
        </div>
        <Badge variant={isAuctionActive ? "destructive" : "secondary"}>
          {rfp?.status?.replace(/_/g, " ")}
        </Badge>
      </div>

      {/* Timeline Stepper */}
      <AuctionTimelineStepper rfp={rfp} />

      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Proposals</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold">{proposals.length}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Bids</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold">{bids.length}</div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Lowest Bid</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex items-end">
            <div className="text-2xl font-bold text-green-600">
              ${getLowestBid().toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col min-h-[120px]">
          <CardHeader className="pb-2 flex-shrink-0">
            <CardTitle className="text-sm font-medium text-muted-foreground">Auction End</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-end">
            {isAuctionActive && rfp ? (
              <AuctionCountdown 
                targetUtc={rfp.auction_end} 
                label="Ends in"
                showFullDate={true}
              />
            ) : rfp ? (
              <>
                <div className="text-sm font-medium">
                  {formatInLondon(rfp.auction_end, "dd MMM yyyy")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatInLondon(rfp.auction_end, "HH:mm (zzz)")}
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Active Vendors Panel - only show during active auction */}
      {isAuctionActive && rfp && (
        <ActiveVendorsPanel 
          rfpId={rfp.id}
          bids={bids.filter(b => b.proposal).map(b => ({
            proposal_id: b.proposal_id,
            created_at: b.created_at,
            status: b.status,
            proposal: { supplier_company_name: b.proposal!.supplier_company_name }
          }))}
          proposals={proposals}
        />
      )}

      {/* Tabs for Proposals, Bids, and Details */}
      <Tabs defaultValue="proposals">
        <TabsList>
          <TabsTrigger value="proposals">Ranked Proposals</TabsTrigger>
          <TabsTrigger value="bids">Bid History</TabsTrigger>
          <TabsTrigger value="details">RFP Details</TabsTrigger>
          <TabsTrigger value="vendors">Vendor Tracking</TabsTrigger>
          <TabsTrigger value="invite">Invite Supplier</TabsTrigger>
        </TabsList>

        <TabsContent value="proposals" className="space-y-4">
          {proposals.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No proposals submitted yet for this RFP.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {proposals.map((proposal) => (
                <BidCard
                  key={proposal.id}
                  proposal={proposal}
                  onViewProposal={handleViewProposal}
                  onRevisedOffer={handleRevisedOffer}
                  onSelect={handleSelectClick}
                  isSelecting={isSelecting}
                  selectedProposalId={proposals.find(p => p.isSelected)?.id || null}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="bids" className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Bid History</h3>
              {isAuctionActive && (
                <div className="flex items-center gap-2 text-sm text-destructive animate-pulse">
                  <div className="h-2 w-2 bg-destructive rounded-full"></div>
                  Live
                </div>
              )}
            </div>
          </div>
          
          {bids.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No bids placed yet
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {bids.map((bid) => {
                const isRequested = bid.status === 'requested';
                return (
                  <Card 
                    key={bid.id} 
                    className={`hover:shadow-md transition-shadow ${isRequested ? 'border-2 border-orange-400 bg-orange-50/30' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-foreground">
                            {bid.proposal?.supplier_company_name}
                          </p>
                          <Badge 
                            variant={isRequested ? 'secondary' : 'outline'} 
                            className={`text-xs mt-1 ${isRequested ? 'bg-orange-100 text-orange-700' : ''}`}
                          >
                            {isRequested ? 'REQUESTED' : bid.source?.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            ${bid.amount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {formatFullDateTime(bid.created_at)}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>RFP Information</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(`/auctions/${rfpId}/rfp`)}
              >
                <FileText className="h-4 w-4 mr-1" />
                View RFP
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {rfp?.matter_name && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Matter Name</p>
                  <p className="text-foreground">{rfp.matter_name}</p>
                </div>
              )}
              {rfp?.region && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Region</p>
                  <p className="text-foreground">{rfp.region.toUpperCase()}</p>
                </div>
              )}
              {rfp?.team && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Team</p>
                  <p className="text-foreground">{rfp.team.charAt(0).toUpperCase() + rfp.team.slice(1)}</p>
                </div>
              )}
              {rfp?.category && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Category</p>
                  <p className="text-foreground">{rfp.category}</p>
                </div>
              )}
              {rfp?.budget_range && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Budget Range</p>
                  <p className="text-foreground">{rfp.budget_range}</p>
                </div>
              )}
              {rfp && (
                <>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Submission Deadline</p>
                    <p className="text-foreground">{formatFullDateTime(rfp.submission_deadline)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Auction Window</p>
                    <p className="text-foreground">
                      {formatFullDateTime(rfp.auction_start)} — {formatFullDateTime(rfp.auction_end)}
                    </p>
                  </div>
                </>
              )}
              {rfp?.min_decrement && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimum Bid Decrement</p>
                  <p className="text-foreground">${rfp.min_decrement}</p>
                </div>
              )}
              {rfp?.min_bid && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Minimum Allowed Bid</p>
                  <p className="text-foreground">${rfp.min_bid}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendors">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Vendor Tracking
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No vendors invited yet</p>
              ) : (
                <div className="space-y-2">
                  {invitations.map((invitation) => {
                    const hasSubmitted = proposals.some(
                      p => p.contact_email.toLowerCase() === invitation.supplier_email.toLowerCase()
                    );
                    return (
                      <div
                        key={invitation.id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-foreground">{invitation.supplier_name}</p>
                          <p className="text-sm text-muted-foreground">{invitation.supplier_email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasSubmitted ? (
                            <div className="flex items-center gap-1.5 text-green-600">
                              <Check className="h-5 w-5" />
                              <span className="text-sm font-medium">Submitted</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-muted-foreground">
                              <X className="h-5 w-5" />
                              <span className="text-sm font-medium">Pending</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invite">
          {rfp && (
            <AddSupplierInvites rfpId={rfp.id} onInvitesSent={fetchData} />
          )}
        </TabsContent>
      </Tabs>

      {/* Revise Offer Dialog */}
      {rfp && (
        <ReviseOfferDialog
          open={reviseOfferData.open}
          onOpenChange={(open) => setReviseOfferData({ ...reviseOfferData, open })}
          proposalId={reviseOfferData.proposalId}
          rfpId={rfp.id}
          currentBid={reviseOfferData.currentBid}
          supplierName={reviseOfferData.supplierName}
          minDecrement={rfp.min_decrement}
          onSuccess={fetchData}
        />
      )}

      {/* Requested Offer Warning Dialog */}
      <AlertDialog 
        open={requestedOfferWarning.open} 
        onOpenChange={(open) => setRequestedOfferWarning({ ...requestedOfferWarning, open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-orange-100 p-2 rounded-full">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <AlertDialogTitle>Pending Requested Offer</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base">
              You have a requested offer of <span className="font-semibold text-foreground">${requestedOfferWarning.requestedBid.toLocaleString()}</span> pending 
              for <span className="font-semibold text-foreground">{requestedOfferWarning.supplierName}</span>.
              <br /><br />
              If you select this bid now, you will be accepting their <span className="font-semibold text-orange-600">original offer of ${requestedOfferWarning.originalBid.toLocaleString()}</span> and not your requested offer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRequestedOfferWarning({ ...requestedOfferWarning, open: false });
                handleSelect(requestedOfferWarning.proposalId);
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Accept Original Offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReverseAuctionView;
